import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { CompanyFilter } from '../components/CompanyFilter';
import { useDateRange } from '../hooks/useDateRange';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { DollarSign, TrendingUp, Clock, Package, Calendar } from 'lucide-react';

const COLORS = ['#0F172A', '#EA580C', '#2563EB', '#16A34A', '#7C3AED', '#DC2626', '#0891B2', '#CA8A04'];

function StatusBadge({ status }) {
  const cls = status === 'actual' ? 'bg-emerald-50 text-emerald-600' : status === 'estimated' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{status}</span>;
}

export default function AnalyticsOverviewPage() {
  const [overview, setOverview] = useState({});
  const [spend, setSpend] = useState({});
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const { dateFrom, dateTo, setDateFrom, setDateTo, dateParams } = useDateRange();

  const fetchData = () => {
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    if (filterSite !== 'all') params.site_id = filterSite;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const qs = new URLSearchParams(params).toString();
    Promise.all([
      api.get(`/analytics/overview?${qs}`),
      api.get(`/analytics/spend?${qs}`),
    ]).then(([o, s]) => { setOverview(o.data); setSpend(s.data); }).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
    fetchData();
  }, []);

  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany, filterSite, dateFrom, dateTo]);

  const kpis = [
    { label: 'Total Spend', value: `₹${(overview.total_spend || 0).toLocaleString()}`, icon: DollarSign, status: overview.data_status?.spend },
    { label: 'Avg Cost/Hr', value: `₹${(overview.avg_cost_per_hour || 0).toFixed(2)}`, icon: Clock, status: overview.data_status?.cost_per_hour },
    { label: 'Cost/100 Hrs', value: `₹${(overview.cost_per_100hrs || 0).toFixed(2)}`, icon: TrendingUp },
    { label: 'Active Products', value: overview.active_products || 0, icon: Package },
  ];

  return (
    <div data-testid="analytics-overview-page">
      <div className="page-header">
        <div><h1 className="page-title">Analytics Overview</h1><p className="text-sm text-slate-500 mt-1">Spend and performance intelligence</p></div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="analytics-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} prefix="analytics" />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {kpis.map(({ label, value, icon: Icon, status }) => (
          <Card key={label} className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1 font-heading">{value}</p>
                {status && <StatusBadge status={status} />}
              </div>
              <Icon size={18} className="text-slate-400" />
            </div>
          </Card>
        ))}
      </div>

      {/* Spend Trend + Supplier Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Spend Trend</h3>
          {(spend.spend_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={spend.spend_trend}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => { const [y, m] = (v || '').split('-'); const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${names[parseInt(m,10)-1] || m} '${(y||'').slice(-2)}`; }} /><YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="spend" fill="#0F172A" radius={[4,4,0,0]} /></BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-16">No spend data</p>}
        </Card>
        <Card className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Spend by Supplier</h3>
          {(spend.spend_by_supplier || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={(spend.spend_by_supplier || []).slice(0,6)} dataKey="spend" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>{(spend.spend_by_supplier || []).slice(0,6).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-16">No data</p>}
        </Card>
      </div>

      {/* Spend by Supplier Table */}
      <Card className="bg-white border border-slate-200 rounded-lg mb-6">
        <div className="px-5 py-3 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-900 font-heading">Spend by Supplier</h3></div>
        <div className="data-table-wrapper border-0 shadow-none rounded-none">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Supplier</TableHead><TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spend</TableHead><TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qty</TableHead><TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead><TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead><TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trials</TableHead></TableRow></TableHeader>
            <TableBody>
              {(spend.spend_by_supplier || []).length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No data</TableCell></TableRow> :
              (spend.spend_by_supplier || []).map(s => (
                <TableRow key={s.supplier_id} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                  <TableCell className="text-sm font-medium">₹{s.spend.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.qty}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.hours}h</TableCell>
                  <TableCell className="text-sm text-slate-700 font-medium">₹{s.cost_per_hour.toFixed(2)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
